import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { API_BASE } from "../lib/api";
import {
  Headphones,
  Play,
  Pause,
  ExternalLink,
  BookOpen,
  GraduationCap,
  ChevronRight,
  Sparkles,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import BrandNav from "../components/BrandNav";

// Voice-slug → human label. Only relevant during Milestone-2 previews.
const VOICE_LABEL = {
  grace_bishop:   "Grace Bishop \u00b7 warm, natural",
  aubrey_ellis:   "Aubrey Ellis \u00b7 confident storyteller",
  rowan_parker:   "Rowan Parker \u00b7 young narrator",
  david_anderson: "David Anderson \u00b7 hushed and gentle",
  james_coleman:  "James Coleman \u00b7 documentary calm",
};

function formatTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00";
  const s = Math.floor(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function Meet() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialVariant = searchParams.get("variant") || null;

  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [variant, setVariant] = useState(initialVariant);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const audioRef = useRef(null);
  const scrubberRef = useRef(null);

  // Fetch metadata whenever slug changes.
  useEffect(() => {
    let alive = true;
    setState({ loading: true, data: null, error: null });
    fetch(`${API_BASE}/api/portraits/${encodeURIComponent(slug)}/metadata`)
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!alive) return;
        if (!res.ok) {
          setState({ loading: false, data: null, error: body.detail || "This portrait audio couldn't be loaded." });
          return;
        }
        setState({ loading: false, data: body, error: null });

        // Do NOT auto-select a variant when a canonical mp3 exists — that
        // would silently swap the user's chosen production voice (Grace Bishop
        // for Ada, David Anderson for Turing) for whichever variant sorts
        // alphabetically first. Only pick a variant when the caller explicitly
        // asked for one via ?variant= in the URL.
        // The variant switcher remains visible below for A/B previews.

        // Analytics: portrait viewed.
        fetch(`${API_BASE}/api/marketing/event`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_type: "portrait_page_view",
            portrait_slug: slug,
            referrer: typeof document !== "undefined" ? document.referrer || "" : "",
          }),
          keepalive: true,
        }).catch(() => { /* swallow */ });
      })
      .catch(() => {
        if (alive) setState({ loading: false, data: null, error: "Network error while loading this page." });
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Build the audio URL from the chosen variant + slug.
  const audioUrl = useMemo(() => {
    if (!state.data) return null;
    const base = `${API_BASE}/api/portraits/${encodeURIComponent(slug)}/audio`;
    return variant ? `${base}?variant=${encodeURIComponent(variant)}` : base;
  }, [slug, variant, state.data]);

  // Pause/reset when variant changes so we always play the newly-chosen voice.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setPlaying(false);
    }
    // Push variant into the URL as ?variant= so the page is shareable.
    if (variant) {
      const next = new URLSearchParams(searchParams);
      next.set("variant", variant);
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => toast.error("Could not start playback."));
      // Analytics: fire once per play button press.
      fetch(`${API_BASE}/api/marketing/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_type: "portrait_audio_played",
          portrait_slug: slug,
          variant: variant || null,
        }),
        keepalive: true,
      }).catch(() => { /* swallow */ });
    } else {
      el.pause();
    }
  };

  const onScrub = (e) => {
    const el = audioRef.current;
    if (!el || !progress.total) return;
    const rect = scrubberRef.current.getBoundingClientRect();
    const rel = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    el.currentTime = rel * progress.total;
  };

  const copyShareLink = async () => {
    try {
      const url = typeof window !== "undefined" ? window.location.href : "";
      await navigator.clipboard.writeText(url);
      toast.success("Link copied \u2014 share this page with anyone.");
    } catch {
      toast.error("Could not copy the link.");
    }
  };

  return (
    <div data-testid="meet-page">
      <BrandNav />
      <main className="ftg-shell ftg-page">
        <div className="ftg-meet">
          {state.loading && (
            <div className="ftg-loader" data-testid="meet-loading">
              <div className="ftg-loader__dot" />
            </div>
          )}

          {!state.loading && state.error && (
            <div className="ftg-verify__card ftg-verify__card--invalid" data-testid="meet-invalid">
              <ShieldAlert size={36} />
              <h1 className="ftg-verify__title">Portrait not found</h1>
              <p className="ftg-verify__body">{state.error}</p>
              <Link to="/" className="ftg-btn ftg-btn--ghost" data-testid="meet-home">Back to home</Link>
            </div>
          )}

          {!state.loading && state.data && (
            <>
              <article className="ftg-meet__card" data-testid="meet-card">
                <div className="ftg-meet__eyebrow">
                  <Sparkles size={14} /> Meet Someone Amazing
                </div>
                <h1 className="ftg-meet__title" data-testid="meet-title">
                  {state.data.name}
                </h1>
                <p className="ftg-meet__lede">
                  A two-minute audio drama for young STEAM learners &mdash; warm narration,
                  gentle music, and three true stories to spark curiosity.
                </p>

                {/* voice-variant switcher (Milestone-2 only shows when >1 variant) */}
                {Array.isArray(state.data.variants) && state.data.variants.length > 1 && (
                  <div className="ftg-meet__variants" data-testid="meet-variants">
                    <div className="ftg-meet__variants-label">Compare narrator voices:</div>
                    <div className="ftg-meet__variant-row">
                      {state.data.variants.map((v) => (
                        <button
                          key={v}
                          type="button"
                          className={`ftg-btn ftg-btn--sm ${v === variant ? "ftg-btn--secondary" : "ftg-btn--ghost"}`}
                          onClick={() => setVariant(v)}
                          data-testid={`meet-variant-${v}`}
                        >
                          {VOICE_LABEL[v] || v}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Player */}
                <div className="ftg-meet__player" data-testid="meet-player">
                  <button
                    type="button"
                    onClick={togglePlay}
                    className={`ftg-meet__play ${playing ? "is-playing" : ""}`}
                    aria-label={playing ? "Pause" : "Play"}
                    data-testid="meet-play-button"
                  >
                    {playing ? <Pause size={22} /> : <Play size={22} />}
                  </button>
                  <div className="ftg-meet__player-body">
                    <div className="ftg-meet__player-title">
                      {playing ? "Playing" : "Listen"}
                      <span className="ftg-meet__player-sub">{state.data.name}</span>
                    </div>
                    <div
                      ref={scrubberRef}
                      className="ftg-meet__scrubber"
                      onClick={onScrub}
                      role="slider"
                      aria-valuemin={0}
                      aria-valuemax={progress.total || 0}
                      aria-valuenow={progress.current || 0}
                      tabIndex={0}
                      data-testid="meet-scrubber"
                    >
                      <div
                        className="ftg-meet__scrubber-fill"
                        style={{ width: `${progress.total ? (100 * progress.current / progress.total) : 0}%` }}
                      />
                    </div>
                    <div className="ftg-meet__player-times">
                      <span>{formatTime(progress.current)}</span>
                      <span>{formatTime(progress.total || state.data.duration_seconds || 0)}</span>
                    </div>
                  </div>
                </div>

                {audioUrl && (
                  <audio
                    ref={audioRef}
                    src={audioUrl}
                    preload="metadata"
                    onLoadedMetadata={(e) => setProgress({ current: 0, total: e.target.duration || 0 })}
                    onTimeUpdate={(e) => setProgress((p) => ({ ...p, current: e.target.currentTime }))}
                    onPlay={() => setPlaying(true)}
                    onPause={() => setPlaying(false)}
                    onEnded={() => setPlaying(false)}
                    data-testid="meet-audio-element"
                  />
                )}

                <div className="ftg-meet__share-row">
                  <button
                    type="button"
                    onClick={copyShareLink}
                    className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                    data-testid="meet-share-copy"
                  >
                    Copy share link
                  </button>
                  {state.data.script_available && (
                    <a
                      href={`${API_BASE}/api/portraits/${slug}/script`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ftg-btn ftg-btn--ghost ftg-btn--sm"
                      data-testid="meet-view-script"
                    >
                      View script
                    </a>
                  )}
                </div>
              </article>

              <aside className="ftg-cta-card" data-testid="meet-cta">
                <div className="ftg-cta-card__eyebrow">Explore FutureTec Global</div>
                <h2 className="ftg-cta-card__title">
                  Curious minds start here.
                  <span className="ftg-cta-card__title-thin"> Give your child 300 audio lessons and 75 chapter books.</span>
                </h2>
                <ul className="ftg-cta-card__bullets">
                  <li><Headphones size={16} /> Warm audio lessons for ages 6&ndash;17.</li>
                  <li><BookOpen size={16} /> 75 printable Deep-Dive Reader chapter books.</li>
                  <li><GraduationCap size={16} /> Real certificates verified globally.</li>
                </ul>
                <div className="ftg-cta-card__actions">
                  <Link to="/" className="ftg-btn ftg-btn--secondary" data-testid="meet-cta-start">
                    Start free preview <ChevronRight size={14} />
                  </Link>
                  <Link to="/#pricing" className="ftg-btn ftg-btn--ghost" data-testid="meet-cta-pricing">
                    <ExternalLink size={14} /> See pricing
                  </Link>
                </div>
              </aside>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
